import { DT } from "../../constants.ts";
import { add } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, setHv } from "../../sim/components.ts";
import { addOverhealth, aim, applyDamage, applyStatus, damageBarrier, explode, hitscan, isEnemy, moveProjectile, playersInRadius, spawnZone } from "../../sim/combat.ts";
import { damageConstruct } from "../../sim/constructs.ts";
import { beginTargeting, targetConfirmed, targetingSlot, targetPoint } from "../../sim/targeting.ts";
import { pull } from "../kitlib.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, zk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, flight, launchProjectile, ownerAlive, ready, secs, trigger, useUlt } from "../kitlib.ts";

export const V = { weather: 0, boost: 1, storming: 2, spare: 3 } as const;
export const PK = { Wind: pk(HeroId.Brainstorm, 0) } as const;
export const ZK = { Hurricane: zk(HeroId.Brainstorm, 0) } as const;
export const FX = { Wind: fxk(HeroId.Brainstorm, 0), Lightning: fxk(HeroId.Brainstorm, 1), Hurricane: fxk(HeroId.Brainstorm, 2) } as const;

const def: HeroDef = {
  id: HeroId.Brainstorm,
  slug: "brainstorm",
  name: "Brainstorm",
  role: Role.Damage,
  color: "#9fb6ff",
  blurb: "Aerial weather controller who swaps between speed and damage auras, then amplifies them.",
  health: 250,
  armor: 0,
  shields: 0,
  radius: 0.4,
  height: 1.85,
  headRadius: 0.23,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 12,
  reloadTime: 1.4,
  ultCost: 3400,
  abilities: {
    [Slot.Primary]: { name: "Gale Thesis", key: "LMB", description: "Launch a fast wind blade that pierces in a line.", cooldown: 0 },
    [Slot.Secondary]: { name: "Bolt Footnote", key: "RMB", description: "Fire a 40m lightning line.", cooldown: 8 },
    [Slot.Ability1]: { name: "Forecast Flip", key: "Shift", description: "Toggle your 15m aura between speed wind and damage thunder.", cooldown: 0 },
    [Slot.Ability2]: { name: "Peer Review", key: "E", description: "Empower the current aura for 8 seconds: stronger speed plus slow, or stronger damage plus lightning.", cooldown: 15 },
    [Slot.Ultimate]: { name: "Eye Exam", key: "Q", description: "Vanish, aim a location, then LMB to emerge as a hurricane for 5 seconds.", cooldown: 0 },
  },
  passive: { name: "Pressure System", description: "Brainstorm can freely fly and shares her current weather aura with nearby allies." },
};

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.weather, 0);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      const boost = hv(c, eid, V.boost);
      if (boost > 0) setHv(c, eid, V.boost, boost - 1);
      if (hv(c, eid, V.storming) > 0) setHv(c, eid, V.storming, hv(c, eid, V.storming) - 1);
      commonInput(sim, eid, input, pressed);
      if (targetingSlot(sim, eid) === Slot.Ultimate) {
        applyStatus(sim, eid, "phased", 0.1, { source: eid });
        applyStatus(sim, eid, "invuln", 0.1, { source: eid });
      }

      const thunder = hv(c, eid, V.weather) > 0.5;
      const empowered = hv(c, eid, V.boost) > 0;
      const radius = 15;
      for (const { eid: a } of playersInRadius(sim, sim.posOf(eid), radius, (e) => !isEnemy(sim, eid, e), false)) {
        if (thunder) applyStatus(sim, a, "boost", 0.2, { boostPct: a === eid ? (empowered ? 0.25 : 0.12) : (empowered ? 0.15 : 0.08), source: eid });
        else applyStatus(sim, a, "haste", 0.2, { hastePct: a === eid ? (empowered ? 0.4 : 0.16) : (empowered ? 0.2 : 0.08), source: eid });
      }
      if (empowered && thunder && sim.tick % 120 === 0) {
        for (const { eid: t } of playersInRadius(sim, sim.posOf(eid), radius, (e) => isEnemy(sim, eid, e), false)) applyDamage(sim, t, 35, eid);
      }
      if (empowered && !thunder) {
        for (const { eid: t } of playersInRadius(sim, sim.posOf(eid), radius, (e) => isEnemy(sim, eid, e), false)) applyStatus(sim, t, "slow", 0.25, { slowPct: 0.15, source: eid });
      }
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(5);
        beginTargeting(sim, eid, Slot.Ultimate);
      }
      if (targetConfirmed(sim, eid, Slot.Ultimate)) {
        const p = targetPoint(sim, eid, Slot.Ultimate).pos;
        c.Pos.x[eid] = p.x;
        c.Pos.y[eid] = p.y;
        c.Pos.z[eid] = p.z;
        c.Ult.active[eid] = secs(5);
        setHv(c, eid, V.storming, secs(5));
        addOverhealth(sim, eid, 450, secs(9.5), eid);
        spawnZone(sim, eid, ZK.Hurricane, p, 8, 5);
        return;
      }
      if (targetingSlot(sim, eid) === Slot.Ultimate) {
        return;
      }
      if (pressed & Btn.Ability1) {
        setHv(c, eid, V.weather, thunder ? 0 : 1);
        action(sim, eid, ActionKind.Cast, 10);
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        setHv(c, eid, V.boost, secs(8));
        action(sim, eid, ActionKind.Cast, 18);
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.AltFire, 12);
        const a = aim(sim, eid, 40);
        const hit = hitscan(sim, eid, a.origin, a.dir, { range: 40, damage: 80, fxKind: FX.Lightning, tracer: true });
        if (!thunder && hit.dealt > 0) addOverhealth(sim, eid, Math.min(75, c.Health.over[eid]! + hit.dealt * 0.25), secs(3), eid);
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = 30;
        action(sim, eid, ActionKind.Fire, 8);
        launchProjectile(sim, eid, PK.Wind, 150, input, { radius: 0.12, seconds: 0.5 });
      }
    },
    movement(sim, eid, input, mods) {
      flight(sim, eid, input, mods, { speed: sim.hero(eid).speed, accel: 28, vertical: 5.5, pitchClimb: true });
    },
  },
  projectiles: {
    [PK.Wind]: {
      visual: { color: "#d8ecff", size: 0.18, trail: true },
      pierce: 1,
      tick(sim, p) {
        const c = sim.c;
        const owner = c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p);
        if (hit) {
          if (ownerAlive(sim, owner)) {
            if (hit.target !== undefined) {
              const dealt = applyDamage(sim, hit.target, 60, owner, { pos: hit.pos, info: { kind: "projectile", sourceKind: PK.Wind } });
              if (hv(c, owner, V.weather) <= 0.5 && dealt > 0) {
                addOverhealth(sim, owner, Math.min(75, c.Health.over[owner]! + dealt * 0.25), secs(3), owner);
              } else {
                const i = owner * 6 + Slot.Secondary;
                c.Abil.cd[i] = Math.max(0, c.Abil.cd[i]! - 30);
              }
            } else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, 60, owner);
            else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 60, owner);
            else explode(sim, owner, hit.pos, { radius: 0.8, damage: 0, fxKind: FX.Wind });
          }
          if ((hit.target !== undefined || hit.construct !== undefined) && c.Projectile.bounces[p]! < 1) {
            c.Projectile.bounces[p] = c.Projectile.bounces[p]! + 1;
            const l = Math.hypot(c.Vel.x[p]!, c.Vel.y[p]!, c.Vel.z[p]!) || 1;
            c.Pos.x[p] = c.Pos.x[p]! + (c.Vel.x[p]! / l) * 0.35;
            c.Pos.y[p] = c.Pos.y[p]! + (c.Vel.y[p]! / l) * 0.35;
            c.Pos.z[p] = c.Pos.z[p]! + (c.Vel.z[p]! / l) * 0.35;
            return;
          }
          sim.destroyEntity(p);
          return;
        }
        if (c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
  },
  zones: {
    [ZK.Hurricane]: {
      visual: { color: "#9fb6ff", dome: true },
      tick(sim, z) {
        const owner = sim.c.Zone.owner[z]!;
        if (!ownerAlive(sim, owner)) return;
        const op = sim.posOf(owner);
        sim.c.Pos.x[z] = op.x;
        sim.c.Pos.y[z] = op.y;
        sim.c.Pos.z[z] = op.z;
        const pos = sim.posOf(z);
        for (const { eid: t } of playersInRadius(sim, pos, 8, (e) => isEnemy(sim, owner, e), false)) {
          applyDamage(sim, t, 160 * DT, owner, { pos });
          pull(sim, t, add(pos, { x: 0, y: 1, z: 0 }), { speed: 8, seconds: 0.12, source: owner });
        }
      },
    },
  },
  fx: {
    [FX.Wind]: { color: "#d8ecff" },
    [FX.Lightning]: { color: "#fff36a" },
    [FX.Hurricane]: { color: "#9fb6ff" },
  },
  targets: {
    [Slot.Ultimate]: { start: "kit", range: 30, radius: 8, timeout: 5, verb: "Teleport" },
  },
};

export default module;
